import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, DestroyRef, EventEmitter, inject, NgZone, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Breakpoints } from '@web/common';
import elementResizeDetectorMaker from 'element-resize-detector';
import { filter, map, startWith, Subject } from 'rxjs';
import { SideNavigationService } from '../service/side-navigation.service';
import { SideNavigationDividerComponent } from './divider/side-navigation-divider.component';
import { SideNavigationMenuItemComponent } from './menu-item/side-navigation-menu-item.component';

const elementResizeDetector = elementResizeDetectorMaker({
  strategy: 'scroll',
});

type SideNavigationState = 'wide' | 'narrow' | 'closed' | 'opened';

@Component({
  selector: 'cas-side-navigation',
  template: `
    <aside
      class="h-100 d-flex flex-column bg-cloud-white justify-content-between"
      [@side-navigationState]="state"
      [class.opened]="!closed">
      <div class="d-flex flex-column my-6 mx-4">
        @for (item of menuItems(); track $index) {
          @if (item.divider?.includes('before')) {
            <cas-side-navigation-divider></cas-side-navigation-divider>
          }
          <cas-side-navigation-menu-item
            [menuItem]="item"
            [state]="state"
            (click)="closeSideNavigation()" />
          @if (item.divider?.includes('after')) {
            <cas-side-navigation-divider></cas-side-navigation-divider>
          }
        }
      </div>
    </aside>
    @if (!closed) {
      <div
        class="side-navigation-overlay"
        [@overlayState]
        (click)="toggle()"></div>
    }
  `,
  styleUrls: ['./side-navigation.component.scss'],
  animations: [
    trigger('side-navigationState', [
      state(
        'narrow',
        style({
          width: '72px',
        }),
      ),
      state(
        'wide',
        style({
          width: '240px',
        }),
      ),
      state('closed', style({ transform: 'translateX(-101%)' })),
      transition('narrow <=> wide', animate('0ms ease-out')),
      transition('* <=> closed', animate('50ms ease-out')),
    ]),
    trigger('overlayState', [transition('* => void', [animate('200ms ease-out', style({ opacity: 0 }))])]),
  ],
  imports: [SideNavigationMenuItemComponent, SideNavigationDividerComponent],
})
export class SideNavigationComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private service = inject(SideNavigationService);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  @Output() width = new EventEmitter<number>();

  menuItems = this.service.menuItems;
  closed = true;

  private resizeEvent$ = new Subject<number>();
  private preResizeEvent: SideNavigationState = this.state;
  private _state: SideNavigationState = 'wide';

  set state(value: SideNavigationState) {
    this.width.emit(value === 'wide' ? 240 : value === 'narrow' ? 72 : 0);
    this._state = value;
  }

  get state() {
    return this._state;
  }

  ngOnInit(): void {
    const filterFns = [
      (width: number): boolean => width < Breakpoints.xxl && this.state === 'wide',
      (width: number): boolean => width >= Breakpoints.xxl && this.preResizeEvent === 'wide',
      (width: number): boolean => width <= Breakpoints.sm && this.state !== 'closed',
      (width: number): boolean => width > Breakpoints.sm && this.state === 'closed',
    ];
    this.ngZone.runOutsideAngular(() => {
      elementResizeDetector.listenTo(<HTMLElement>document.querySelector('body'), () =>
        this.resizeEvent$.next(window.innerWidth),
      );
      this.resizeEvent$
        .pipe(
          filter(width => filterFns.some(fn => fn(width))),
          map(width => (width > Breakpoints.xxl ? 'wide' : width > Breakpoints.sm ? 'narrow' : 'closed')),
          startWith('wide'),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(state => {
          this.preResizeEvent = this.state;
          this.ngZone.run(() => (this.state = state as SideNavigationState));
          this.state = state as SideNavigationState;
        });
    });
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.closeSideNavigation());
  }

  toggle(close?: true) {
    if (window.innerWidth < Breakpoints.xxl) {
      this.closed = close ?? !this.closed;
      this.state = this.closed ? (window.innerWidth > Breakpoints.sm ? 'narrow' : 'closed') : 'wide';
    } else {
      this.state = this.state === 'narrow' || this.state === 'closed' ? 'wide' : 'narrow';
    }
  }

  closeSideNavigation() {
    window.innerWidth < Breakpoints.xxl && this.toggle(true);
  }
}
